// Business Logic for defining the various "buckets" of upcoming tasks.
// Like "All", "Overdue", and "Upcoming".
//
// Routines add a new property to a Task: bucketName.
//
// The "Soon" window is PER ITEM. `TaskPogoItem.soonDays` / `soonCounter` set
// how far ahead (in days / in counter units) a task counts as due soon; a
// null column means the default (30 days / 50 units). `soonThresholds(item)`
// is the one place those columns are interpreted, and every routine that
// buckets a task takes the item (or accepts its absence, meaning the
// defaults) so the item page, the items list, the task list query and the
// email daemon cannot disagree about one item's window.
//

import * as Common from './Common.mjs';
import * as Urls from './Urls.mjs';

// Lookup up the given counterId in this item's ItemCounter, and return it's counter.
// Returns -1 if not found.
export function counterFromId(item, id) {
  let itemCounter = Common.itemCounterFromId(item, id);
  if (itemCounter) {
      return itemCounter.counter;
  }
  return -1;
}

function counterFromCounters(counters, id) {
  let c = counters[id];
  if (c) {
    return c.counter;
  }
  return -1;
}

// Given a task and range, return true if the task should be added to the range.
function shouldAdd(task, range, counterNow) {
  // All tasks? Just add...
  if (range.allTasks) {
    return true;
  }

  const greaterThanCounterRemaining = range.gtCounter;
  const lessThanOrEqualCounterRemaining = range.lteCounter;
  const greaterThanDate = range.gtDate;
  const lessThanOrEqualDate = range.lteDate;
  const dueCounter = task.dueCounter;
  const dueDate = Common.dateStringToDate(task.dueDate);

  // Not all items are due based on counter. Skip if this one isn't.
  if (null !== dueCounter) {
    // and compare its counter to the counter based on.
    const counterRemaining = dueCounter - counterNow;

    // If in range, return it.
    if ((counterRemaining > greaterThanCounterRemaining) && (counterRemaining <= lessThanOrEqualCounterRemaining)) {
      return true;
    }
  }

  // Due date?
  if (null !== dueDate) {
    let dueTime = dueDate.getTime();

    if ((dueTime > greaterThanDate.getTime()) && (dueTime <= lessThanOrEqualDate.getTime())) {
      return true;
    }
  }

  return false;
}

// Look at a task and return true if it comes due within a given counter or date range.
function shouldAddCounterMap(task, counterMap, range) {
  let counterNow = counterFromCounters(counterMap, task.itemCounterId);
  return shouldAdd(task, range, counterNow);
}


// The window an item falls back to when it has not set its own.
export const defaultSoonDays = 30;
export const defaultSoonCounter = 50;

// The per-item due-soon window: `{ days, counter }`. A finite number greater
// than zero on the item's `soonDays` / `soonCounter` wins; anything else
// (null, absent, 0, NaN, a string, no item at all) is the default. This is
// the ONLY reader of those two columns -- the ItemTracking.mjs rule.
function positiveOr(value, fallback) {
  const n = typeof value === 'number' ? value : NaN;
  return Number.isFinite(n) && n > 0 ? n : fallback;
}
export function soonThresholds(item) {
  return {
    days: positiveOr(item?.soonDays, defaultSoonDays),
    counter: positiveOr(item?.soonCounter, defaultSoonCounter),
  };
}

// The word for the counter half of the Soon heading. A counter is whatever
// the item measures -- hours, miles, cycles -- so the heading names the
// item's own counter when it has exactly one (and the list is known to be
// complete), and says "units" otherwise: an item with Engine Hours AND
// Nautical Miles has one window that applies to both, and naming either
// would be a lie about the other. The old heading said "hour" for everyone.
export function soonCounterUnit(item) {
  const connection = item?.itemCounter;
  const counters = connection?.items;
  if (Array.isArray(counters) && counters.length === 1 && !connection.nextToken) {
    const name = typeof counters[0]?.name === 'string' ? counters[0].name.trim() : '';
    if (name) return name;
  }
  return 'units';
}

// The Soon bucket's heading for a given window -- "30 days / 50 units" by
// default, "30 days / 50 Hours" for an item whose one counter is Hours.
export function soonLabel({ days, counter } = soonThresholds(null), unit = 'units') {
  return `${days} ${days === 1 ? 'day' : 'days'} / ${counter} ${unit}`;
}

export const nameOverdue = 'Overdue';
// A bucket NAME is an identity, compared with === all over the app and
// persisted in `TaskPogoEmailSubscription.taskBucketSnapshots`. It used to
// be the default window's label ("30 day/50 hour"), which would both lie
// for an item with its own window and stop matching the snapshots already
// stored -- so the identity is a plain word now and `legacyNameSoon` is
// what those stored snapshots still say. `bucketHeading` is the display
// string.
export const nameSoon    = 'Soon';
export const legacyNameSoon = '30 day/50 hour';
export const nameAll     = 'All Tasks';

// Display heading for a bucket, per item: the Soon heading names the item's
// own window.
export function bucketHeading(name, item = null) {
  if (name === nameSoon) return soonLabel(soonThresholds(item), soonCounterUnit(item));
  return name;
}

// "Not Due" snapshot label used by transition emails. A task that is neither
// overdue nor due-soon falls in this severity tier (= 0). Severity comparison
// drives the worsening-only digest in `computeBucketTransitions`.
export const nameNotDue = 'notDue';

// Human-readable label for a bucket name used in transition emails. The
// canonical `nameNotDue` value (`'notDue'`) is an internal snapshot/severity
// key and looks like code to a reader, so render it as "Not Due". Other bucket
// names (`nameOverdue`, `nameSoon`) are already presentable as-is.
export function humanBucketName(name) {
  if (name === nameNotDue) return 'Not Due';
  if (name === legacyNameSoon) return nameSoon;
  return name;
}

// Severity rank used to compare buckets for transition digests. Higher means
// worse. `nameAll` collapses to `nameNotDue` (severity 0) because tasks that
// only land in "All Tasks" are not Overdue or Soon. A snapshot written before
// the Soon identity changed still carries `legacyNameSoon`, and it must rank
// as Soon or every such task would re-fire as a "worsening" on the next run.
export function bucketSeverity(name) {
  if (name === nameOverdue) return 2;
  if (name === nameSoon || name === legacyNameSoon) return 1;
  return 0;
}

// Given a date, return a new date at local start-of-day. Pure: never mutates
// the caller's Date (callers such as the date pickers pass an object they may
// keep using, so this clones before zeroing the time).
export function dateMidnight(date) {
  const midnight = new Date(date);
  midnight.setHours(0, 0, 0, 0);
  return midnight;
}
// Builds a date object, sets hours to midnight.
export function todayMidnight() {
  return dateMidnight(new Date());
}

function soonDate(days) {
  const today = todayMidnight();
  return new Date(today.getTime() + days * 1000 * 60 * 60 * 24);
}


// Returns a GraphQL predicate that can be used to find tasks that are due soon
// because of their due date, over the item's window (the default with no item).
export function graphQLSoon(item = null) {
  const { days } = soonThresholds(item);
  let dueDate = {
    between: [
      Common.databaseDateTimeString(todayMidnight()),
      Common.databaseDateTimeString(soonDate(days)) ]
  }

  return dueDate;
}

// Returns an array of "ranges". Built at runtime, since need current date/time
// and the item's window. `heading` is the display string; `name` is the
// identity.
function buildRanges(item = null) {
  const defaultTasksToShow = 3;
  const today = todayMidnight();
  const thresholds = soonThresholds(item);

  return [
    {
      noDuplicate: true,
      allTasks: false,
      name: nameOverdue,
      heading: bucketHeading(nameOverdue, item),
      icon: "fa-exclamation-circle",
      color: "text-danger",
      numTasksToShow: defaultTasksToShow,
      gtCounter: -999999, 
      lteCounter: 0, 
      gtDate: Common.minDate, 
      lteDate: today,
      tasksUrl: Urls.bucketNameOverdue
    },
    { 
      noDuplicate: true,  
      allTasks: false,
      name: nameSoon,
      heading: bucketHeading(nameSoon, item),
      icon: "fa-hourglass-half",
      color: "text-warning",
      numTasksToShow: defaultTasksToShow,
      gtCounter: 0,
      lteCounter: thresholds.counter,
      gtDate: today,
      lteDate: soonDate(thresholds.days),
      tasksUrl: Urls.bucketNameSoon
    },
    {
      noDuplicate: true,
      allTasks: true,
      name: nameAll,
      heading: bucketHeading(nameAll, item),
      icon: "fa-info",
      color: "text-primary",
      numTasksToShow: defaultTasksToShow,
      gtCounter: -999999,
      lteCounter: 999999, 
      gtDate: Common.minDate,
      lteDate: Common.maxDate,
      tasksUrl: Urls.bucketNameAll
    }
  ];
}

export function taskUrlToBucketName(url) {
  switch (url) {
    case Urls.bucketNameOverdue:
      return nameOverdue;
    case Urls.bucketNameSoon:
      return nameSoon;
    default:
      return nameAll;
  }
}


// Walk thru the list of tasks and put them in to buckets to show.
function getUpcomingTasks(tasks, counterMap, item) {
  const ranges = buildRanges(item);

  // Start with a bunch of empty ranges
  var rangeTasks = ranges.map( (range) => {
    return {
      name: range.name,
      heading: range.heading,
      icon: range.icon,
      color: range.color, 
      numTasksToShow: 
      range.numTasksToShow, 
      tasksUrl: range.tasksUrl,
      tasks: []
    };
  });

  // For every task, look at the counters/date it is due.
  for (var i=0; i < tasks.items.length; i++) {
    const task = tasks.items[i];

    // Task hasn't been added anywhere yet.
    var didAdd = false;

    // Now go thru the various range buckets and add the task where in to each
    // range it belongs to.
    for (var j=0; j < ranges.length; j++) {
      let range = ranges[j];

      // If this range doesn't allow duplicates, and the task has already been added,
      // move on.
      if (range.noDuplicate && didAdd) {
        continue;
      }

      // Add to this bucket if it's the all tasks, or the task is due in the range.
      let add = shouldAddCounterMap(task, counterMap, range);

      // If added to a range (except all-tasks), mark the task as in the bucket. 
      if (add && !range.allTasks) {
        task.bucketName = range.name;
      }

      // Item due? Add to the range tasks and remember it was added.
      if (add) {
        rangeTasks[j].tasks.push(task);
        didAdd = true;
      }
    }
  }
  return rangeTasks;
}


// Given an array of counters, make a counterMap.
export function createCounterMapFromCounters(counters) {
  // Create a map of counters id -> value.
  let counterMap = {};

  for (var i=0; i < counters.length; i++) {
    counterMap[counters[i].id] = counters[i];
  }

  return counterMap;
  
}


// Given an item, create a map of counter ID -> Counter.
export function createCounterMapFromItem(item) {
  return createCounterMapFromCounters(item.itemCounter.items);
}



// Returns an array of objects:
//    name: Name of bucket (Like "Overdue"),
//    heading: Display heading (the Soon bucket names the item's window).
//    icon: Icon to use for bucket (like an exclamation point).
//    color: Color to use to display tasks (like text-danger).
//    numTasksToShow: # of tasks from this bucket to show by default.
//    tasksUrl: The last part of the .../tasks/ URL, used by Tasks.jsx.
//    tasks: []  Array of tasks in this bucket.
// The tasks are also modified to add a property "bucketName" that has
// the name of the first bucket the task belongs to.
export function taskBuckets(item) {
  if ((null === item) || (null === item.tasks) || (null === item.tasks.items)) {
    // No tasks, no buckets...
    return [];
  }

  let counterMap = createCounterMapFromItem(item);

  // Sort 'em, over this item's window.
  return getUpcomingTasks(item.tasks, counterMap, item);
}


// Given a status text, return a dictionary for the StatusBadge component.
export function taskStatusToBadge(status) {
  switch (status) {
  case nameOverdue:
    return {name: 'Overdue',     style: 'badge-danger'};
  case nameSoon:
    return {name: 'Soon',        style: 'badge-warning'};
    case nameAll:
      default:
    return {name: 'Not Due',     style: 'badge-primary'};
  }
}

// Given a task and an array of ranges and counterMap, update the task's bucketName.
export function updateTaskBucketNameRangesCounterMap(task, ranges, counterMap) {
  // Now go thru the various range buckets and see if the task belongs to it.
  for (var j=0; j < ranges.length; j++) {
    if (shouldAddCounterMap(task, counterMap, ranges[j])) {
      // Found the bucket, update the task's bucketName.
      task.bucketName = ranges[j].name;
      break;
    }
  }
}

// Update an array of task's bucketName. `item` supplies the due-soon window;
// omitted means the default.
export function updateTaskBucketNameInTaskList(tasks, counterMap, item = null) {
  const ranges = buildRanges(item);

  for (var i=0; i < tasks.length; i++) {
    updateTaskBucketNameRangesCounterMap(tasks[i], ranges, counterMap);
  }
}

// Updates a task's bucketName. `item` supplies the due-soon window.
export function updateTaskBucketName(task, counterMap, item = null) {
  const ranges = buildRanges(item);
  updateTaskBucketNameRangesCounterMap(task, ranges, counterMap);
}


// Adds bucketName to an array of task. The task must have been retrieved from the database
// with a dueCounter, dueDate, and itemCounter.counter. `item` supplies the
// due-soon window (only `soonDays` / `soonCounter` are read off it).
export function addBucketNameToTasks(tasks, item = null) {
  const ranges = buildRanges(item);

  for (var i=0; i < tasks.length; i++) {
    let task = tasks[i];
    for (var j=0; j < ranges.length; j++) {
      const counterNow = task.itemCounter ? task.itemCounter.counter : 0;
      if (shouldAdd(task, ranges[j], counterNow)) {
        // Found the bucket, update the task's bucketName.
        task.bucketName = ranges[j].name;
        break;
      }
    }
  }
}

// Rank used to sort tasks "overdue first" on the All Tasks page. Lower = more
// urgent. Mirrors the bucket assignment in `addBucketNameToTasks` so the row
// order matches the badge: a task lands in its worst-of-{date, counter}
// bucket and rows are grouped by that bucket.
function bucketRank(bucketName) {
  if (bucketName === nameOverdue) return 0;
  if (bucketName === nameSoon) return 1;
  return 2;
}

// Counter "remaining" for a task: how many counter units until it comes due.
// Negative = past due. Returns +Infinity when the task isn't counter-driven
// or we don't know the current counter, so date-only / unknown tasks sort
// after counter-driven ones at the same date.
function counterRemaining(task) {
  const dueCounter = task?.dueCounter;
  if (dueCounter == null || !Number.isFinite(Number(dueCounter))) {
    return Infinity;
  }
  const counterNow = task?.itemCounter?.counter;
  if (counterNow == null || !Number.isFinite(Number(counterNow))) {
    return Infinity;
  }
  return Number(dueCounter) - Number(counterNow);
}

function dueDateMillis(task) {
  const d = Common.dateStringToDate(task?.dueDate);
  return d ? d.getTime() : Infinity;
}

// Comparator for the "Overdue First (Date then Counter)" sort. Tasks must
// already have `bucketName` set (e.g. via `addBucketNameToTasks`). Order:
//   1. Bucket: Overdue → Soon → Not Due (worst-of date or counter wins,
//      because that's how `bucketName` is assigned).
//   2. Earliest `dueDate` first; tasks without a date sort after dated ones.
//   3. Smallest counter remaining first (most overdue / soonest); tasks
//      without a counter sort after counter-driven ones.
export function compareTasksOverdueFirst(a, b) {
  const rankDiff = bucketRank(a?.bucketName) - bucketRank(b?.bucketName);
  if (rankDiff !== 0) return rankDiff;

  const dateDiff = dueDateMillis(a) - dueDateMillis(b);
  if (dateDiff !== 0 && !Number.isNaN(dateDiff)) return dateDiff;

  const counterDiff = counterRemaining(a) - counterRemaining(b);
  if (counterDiff !== 0 && !Number.isNaN(counterDiff)) return counterDiff;

  return 0;
}

// Returns an array of ranges that have numTasks in them.
export function rangesForTasks(tasks, item = null) {
  let ranges = buildRanges(item);

  ranges.forEach((range) => {
    range.numTasks = 0;
  });
  
  tasks.forEach((task) => {
    const counterNow = task.itemCounter ? task.itemCounter.counter : 0;

    ranges.forEach((range) => {
      if (shouldAdd(task, range, counterNow)) {
        range.numTasks = range.numTasks + 1;
      }
    });
  });
  return ranges;
}
